<template>
  <div class="flex min-h-screen">
    <!-- Left: repeating background (hidden on mobile) -->
    <div
      class="hidden min-h-screen flex-[0_0_40%] bg-gray-100 bg-[url('/images/login-bg-pattern.webp')] bg-[length:750px_auto] bg-right bg-repeat md:block"
      aria-hidden="true"
    />

    <!-- Right: yellow panel with consent content -->
    <div class="flex min-h-screen flex-1 items-center justify-center bg-[#F9BE00] p-8">
      <div class="flex w-full max-w-[420px] flex-col items-center text-center">
        <a
          :href="config.websiteUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="mb-10 block"
        >
          <img src="/images/phottr-logo.svg" alt="Phottr" class="h-24 w-[150px]" />
        </a>

        <template v-if="loading">
          <ProgressSpinner
            style="width: 40px; height: 40px"
            stroke-width="4"
            animation-duration="1s"
            class="[&_.p-progressspinner-circle]:!stroke-black"
          />
        </template>

        <template v-else-if="errorState === 'forbidden'">
          <i class="pi pi-lock mb-4 !text-4xl text-black" />
          <h1 class="mb-2 !text-[35px] font-semibold text-black">Access denied</h1>
          <p class="text-lg text-black">
            Your account does not have permission to authorize this connection.
          </p>
        </template>

        <template v-else-if="errorState === 'notFound'">
          <i class="pi pi-exclamation-triangle mb-4 !text-4xl text-black" />
          <h1 class="mb-2 !text-[35px] font-semibold text-black">Request expired</h1>
          <p class="text-lg text-black">
            This authorization request is invalid or has expired. Start the connection again from
            your MCP client.
          </p>
        </template>

        <template v-else-if="decided">
          <i class="pi pi-check-circle mb-4 !text-4xl text-black" />
          <p class="text-lg text-black">Redirecting back to {{ context?.clientName }}…</p>
        </template>

        <template v-else>
          <h1 class="mb-2 !text-[35px] font-semibold text-black">Authorize access</h1>
          <p class="mb-8 text-lg text-black">
            <span class="font-semibold">{{ context?.clientName }}</span> wants to access
            <span class="font-semibold">{{ context?.resourceName }}</span> as
            <span class="font-semibold">{{ user?.email }}</span
            >.
          </p>

          <ul
            class="m-0 mb-10 flex w-full list-none flex-col gap-3 rounded-2xl bg-white p-5 text-left"
          >
            <li
              v-for="scope in context?.scopes"
              :key="scope.id"
              class="flex items-start gap-3 text-sm"
              :class="scope.sensitive ? 'font-semibold text-[#A33A00]' : 'text-black'"
            >
              <i
                class="mt-0.5 shrink-0"
                :class="scope.sensitive ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'"
              />
              <span>{{ scope.description }}</span>
            </li>
          </ul>

          <div class="flex w-full flex-col items-center gap-3">
            <button
              type="button"
              class="inline-flex w-full max-w-[280px] cursor-pointer items-center justify-center gap-3 rounded-full border-0 bg-black py-3.5 px-5 text-base font-medium text-white transition-colors hover:bg-[#1a1a1a] active:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="submitting"
              @click="decide(true)"
            >
              <i v-if="submitting" class="pi pi-spin pi-spinner" />
              <span>Approve</span>
            </button>
            <button
              type="button"
              class="inline-flex w-full max-w-[280px] cursor-pointer items-center justify-center gap-3 rounded-full border-2 border-black bg-transparent py-3 px-5 text-base font-medium text-black transition-colors hover:bg-black/10 active:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="submitting"
              @click="decide(false)"
            >
              Deny
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import ProgressSpinner from 'primevue/progressspinner';
import type { OauthAuthorizeContextDto } from '@/api/generated';
import { useApiClient } from '@/api/apiClient';
import { useAuth } from '@/modules/auth/auth';
import { config } from '@/config';

const { api } = useApiClient();
const { user } = useAuth();
const route = useRoute();

const txn = String(route.query.txn ?? '');
const context = ref<OauthAuthorizeContextDto>();
const loading = ref(true);
const submitting = ref(false);
const decided = ref(false);
const errorState = ref<'forbidden' | 'notFound' | null>(null);

async function loadContext() {
  if (!txn) {
    errorState.value = 'notFound';
    loading.value = false;
    return;
  }
  try {
    const { data, error, response } = await api.oauthControllerAuthorizeContext({
      query: { txn },
    });
    if (error || !data) {
      errorState.value = response?.status === 403 ? 'forbidden' : 'notFound';
      return;
    }
    context.value = data;
  } finally {
    loading.value = false;
  }
}

async function decide(approve: boolean) {
  submitting.value = true;
  try {
    const { data, error, response } = await api.oauthControllerDecide({
      body: { txn, approve },
    });
    if (error || !data) {
      errorState.value = response?.status === 403 ? 'forbidden' : 'notFound';
      return;
    }
    decided.value = true;
    window.location.href = data.redirectUrl;
  } finally {
    submitting.value = false;
  }
}

loadContext();
</script>
